/**
 * Energy Certificate Simulator - Form Hook
 *
 * Custom hook for managing the simulator wizard form state
 * using Mantine's useForm for validation and state management
 */

import { useForm } from '@mantine/form';
import { useState, useCallback, useEffect } from 'react';
import type {
  SimulatorInput,
  SimulatorResult,
  WizardStep,
  SimulatorFormErrors,
} from '../types';
import { calculateEP } from '../utils/epCalculation';
import {
  useSimulatorFormStorage,
  DEFAULT_SIMULATOR_INPUT,
  useSavedSimulations,
} from './useLocalStorage';

// =============================================================================
// WIZARD STEPS
// =============================================================================

export const WIZARD_STEPS: WizardStep[] = [
  'building',
  'insulation',
  'heating',
  'renewables',
  'results',
];

/**
 * Get step index from step name
 */
export function getStepIndex(step: WizardStep): number {
  return WIZARD_STEPS.indexOf(step);
}

/**
 * Get step name from index
 */
export function getStepByIndex(index: number): WizardStep {
  return WIZARD_STEPS[index] ?? 'building';
}

// =============================================================================
// FORM VALIDATION
// =============================================================================

/**
 * Validate form values for current step
 */
function validateStep(values: SimulatorInput, step: WizardStep): SimulatorFormErrors {
  const errors: SimulatorFormErrors = {};

  switch (step) {
    case 'building':
      if (values.usableArea < 20 || values.usableArea > 500) {
        errors.usableArea = 'Powierzchnia musi być między 20 a 500 m²';
      }
      if (!values.buildingPeriod) {
        errors.buildingPeriod = 'Wybierz okres budowy';
      }
      if (!values.buildingType) {
        errors.buildingType = 'Wybierz typ budynku';
      }
      if (values.floors < 1 || values.floors > 10) {
        errors.floors = 'Liczba kondygnacji musi być między 1 a 10';
      }
      if (!values.occupants || values.occupants < 1 || values.occupants > 10) {
        errors.occupants = 'Wybierz liczbę mieszkańców';
      }
      break;

    case 'insulation':
      if (!values.renovationStatus) {
        errors.renovationStatus = 'Wybierz status termomodernizacji';
      }
      if (values.renovationStatus !== 'none' && !values.renovationYear) {
        errors.renovationYear = 'Podaj rok termomodernizacji';
      }
      if (values.renovationYear && (values.renovationYear < 1990 || values.renovationYear > 2024)) {
        errors.renovationYear = 'Rok musi być między 1990 a 2024';
      }
      if (!values.wallInsulation) {
        errors.wallInsulation = 'Wybierz stan izolacji ścian';
      }
      if (!values.roofInsulation) {
        errors.roofInsulation = 'Wybierz stan izolacji dachu';
      }
      if (!values.windowType) {
        errors.windowType = 'Wybierz typ okien';
      }
      break;

    case 'heating':
      if (!values.heatingSystem) {
        errors.heatingSystem = 'Wybierz źródło ogrzewania';
      }
      if (!values.systemAge) {
        errors.systemAge = 'Wybierz wiek instalacji';
      }
      if (!values.hotWaterSource) {
        errors.hotWaterSource = 'Wybierz źródło CWU';
      }
      if (values.hasSecondaryHeating) {
        if (!values.secondaryHeatingSystem) {
          errors.secondaryHeatingSystem = 'Wybierz dodatkowe źródło ogrzewania';
        }
        if (!values.secondaryHeatingShare || values.secondaryHeatingShare < 10 || values.secondaryHeatingShare > 50) {
          errors.secondaryHeatingShare = 'Udział dodatkowego źródła musi być między 10% a 50%';
        }
      }
      break;

    case 'renewables':
      if (values.hasPV && (!values.pvPower || values.pvPower <= 0)) {
        errors.pvPower = 'Podaj moc instalacji PV (kWp)';
      }
      if (values.hasPV && values.pvPower && values.pvPower > 50) {
        errors.pvPower = 'Moc PV nie może przekroczyć 50 kWp';
      }
      break;
  }

  return errors;
}

// =============================================================================
// MAIN FORM HOOK
// =============================================================================

export interface UseSimulatorFormReturn {
  /** Mantine form instance */
  form: ReturnType<typeof useForm<SimulatorInput>>;
  /** Current wizard step */
  currentStep: WizardStep;
  /** Current step index (0-based) */
  currentStepIndex: number;
  /** Total number of steps (excluding results) */
  totalSteps: number;
  /** Whether on results step */
  isOnResults: boolean;
  /** Calculation results (null until calculated) */
  result: SimulatorResult | null;
  /** Whether form is valid for current step */
  isStepValid: boolean;
  /** Whether EP calculation is in progress */
  isCalculating: boolean;
  /** Navigation functions */
  navigation: {
    goToNextStep: () => boolean;
    goToPreviousStep: () => void;
    goToStep: (step: WizardStep) => void;
    restart: () => void;
  };
  /** Actions */
  actions: {
    calculate: () => SimulatorResult;
    saveResult: (name?: string) => string;
    loadSaved: (id: string) => boolean;
    clearForm: () => void;
    /** Focus the first field with an error, returns field name or null */
    focusFirstError: () => string | null;
  };
  /** Saved simulations count */
  savedCount: number;
}

/**
 * Main hook for simulator form management
 */
export function useSimulatorForm(): UseSimulatorFormReturn {
  // State
  const [currentStep, setCurrentStep] = useState<WizardStep>('building');
  const [result, setResult] = useState<SimulatorResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // LocalStorage integration
  const { formState, setFormState, clearFormState } = useSimulatorFormStorage();
  const { saveSimulation, getSimulation, count: savedCount } = useSavedSimulations();

  // Mantine form with inline validation
  const form = useForm<SimulatorInput>({
    mode: 'controlled',
    initialValues: formState,
    validate: (values) => validateStep(values, currentStep),
    validateInputOnBlur: true, // Validate when field loses focus
    validateInputOnChange: true, // Validate as user types (real-time feedback)
  });

  // Auto-save form state on change
  useEffect(() => {
    setFormState(form.values);
  }, [form.values, setFormState]);

  // Calculate step info
  const currentStepIndex = getStepIndex(currentStep);
  const totalSteps = WIZARD_STEPS.length - 1; // Exclude 'results'
  const isOnResults = currentStep === 'results';

  // Validate current step
  const isStepValid = Object.keys(validateStep(form.values, currentStep)).length === 0;

  // Navigation functions
  const goToNextStep = useCallback((): boolean => {
    // Validate current step
    const errors = validateStep(form.values, currentStep);
    form.setErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < WIZARD_STEPS.length) {
      const nextStep = WIZARD_STEPS[nextIndex];
      setCurrentStep(nextStep);

      // Calculate result when reaching results step
      if (nextStep === 'results') {
        setIsCalculating(true);
        const calculatedResult = calculateEP(form.values);
        setResult(calculatedResult);
        setIsCalculating(false);
      }

      return true;
    }

    return false;
  }, [currentStep, currentStepIndex, form]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(WIZARD_STEPS[prevIndex]);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((step: WizardStep) => {
    const targetIndex = getStepIndex(step);
    const currentIdx = getStepIndex(currentStep);

    // Can only go back or to current step freely
    // Going forward requires validation
    if (targetIndex <= currentIdx) {
      setCurrentStep(step);
    }
  }, [currentStep]);

  const restart = useCallback(() => {
    form.reset();
    form.setValues(DEFAULT_SIMULATOR_INPUT);
    setCurrentStep('building');
    setResult(null);
    clearFormState();
  }, [form, clearFormState]);

  // Action functions
  const calculate = useCallback((): SimulatorResult => {
    const calculatedResult = calculateEP(form.values);
    setResult(calculatedResult);
    return calculatedResult;
  }, [form.values]);

  const saveResult = useCallback(
    (name?: string): string => {
      const currentResult = result ?? calculateEP(form.values);
      return saveSimulation({
        name,
        input: form.values,
        result: currentResult,
      });
    },
    [form.values, result, saveSimulation]
  );

  const loadSaved = useCallback(
    (id: string): boolean => {
      const saved = getSimulation(id);
      if (saved) {
        form.setValues(saved.input);
        if (saved.result) {
          setResult(saved.result);
          setCurrentStep('results');
        } else {
          setCurrentStep('building');
        }
        return true;
      }
      return false;
    },
    [form, getSimulation]
  );

  const clearForm = useCallback(() => {
    form.reset();
    form.setValues(DEFAULT_SIMULATOR_INPUT);
    setResult(null);
    clearFormState();
  }, [form, clearFormState]);

  /**
   * Focus the first field with an error
   * Returns the field name or null if no errors
   */
  const focusFirstError = useCallback((): string | null => {
    const errorFields = Object.keys(form.errors);
    if (errorFields.length === 0) {
      return null;
    }

    // Get the first error field and try to focus it
    const firstErrorField = errorFields[0];

    // Try to find and focus the element in the DOM
    // This will work with Mantine form inputs that have proper name attributes
    if (typeof document !== 'undefined') {
      const element = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
      if (element && typeof element.focus === 'function') {
        element.focus();
        element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }

    return firstErrorField;
  }, [form.errors]);

  return {
    form,
    currentStep,
    currentStepIndex,
    totalSteps,
    isOnResults,
    result,
    isStepValid,
    isCalculating,
    navigation: {
      goToNextStep,
      goToPreviousStep,
      goToStep,
      restart,
    },
    actions: {
      calculate,
      saveResult,
      loadSaved,
      clearForm,
      focusFirstError,
    },
    savedCount,
  };
}
